/**
 * Kiosks run the SPA for days without a full reload, so a new deploy would
 * never be picked up. The home screen calls this: if the deployed build differs
 * from the one running, reload to load it (safe there — no session in progress).
 *
 * The build is identified by its hashed main bundle (CRA's asset-manifest.json).
 */

const RELOAD_GUARD_KEY = "app-version-reload-target";

const getRunningMainBundle = (): string | null => {
  const script = Array.from(document.scripts).find((s) =>
    /\/static\/js\/main\.[^/]+\.js$/.test(s.src)
  );
  return script ? new URL(script.src).pathname : null;
};

export const reloadIfNewBuildAvailable = async (): Promise<void> => {
  // Dev server has no hashed bundle / asset manifest.
  if (process.env.NODE_ENV !== "production") return;

  const running = getRunningMainBundle();
  if (!running) return;

  try {
    const response = await fetch(
      `${process.env.PUBLIC_URL || ""}/asset-manifest.json`,
      { cache: "no-store" }
    );
    if (!response.ok) return;
    const manifest = (await response.json()) as { files?: Record<string, string> };
    const deployedMain = manifest.files?.["main.js"];
    if (!deployedMain) return;

    const deployed = new URL(deployedMain, window.location.origin).pathname;
    if (deployed === running) {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
      return;
    }

    // Avoid a reload loop if a stale cache keeps serving the old index.html.
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === deployed) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, deployed);
    window.location.reload();
  } catch {
    // Offline or manifest unavailable: keep running the current build.
  }
};
